import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Suspense, lazy, useEffect, useMemo, useRef } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { apiClient, setApiAuthFailureHandler } from "@/api/client";
import { setAnalyticsContext, normalizeRouteTemplate } from "@/analytics/context";
import { trackEvent, trackPageView } from "@/analytics/events";
import { initAnalytics, setAnalyticsUser, setAnalyticsUserProperties } from "@/analytics/ga4";
import { ToastHost } from "@/components/common/ToastHost";
import { AppShell } from "@/components/layout/AppShell";
import { BackendSyncBridge } from "@/components/system/BackendSyncBridge";
import { useTheme } from "@/hooks/useTheme";
import { useAppStore } from "@/state/appStore";

const ActivityPage = lazy(() => import("@/routes/ActivityPage").then((mod) => ({ default: mod.ActivityPage })));
const AgentDetailsPage = lazy(() => import("@/routes/AgentDetailsPage").then((mod) => ({ default: mod.AgentDetailsPage })));
const AgentsPage = lazy(() => import("@/routes/AgentsPage").then((mod) => ({ default: mod.AgentsPage })));
const ArchivePage = lazy(() => import("@/routes/ArchivePage").then((mod) => ({ default: mod.ArchivePage })));
const AuthPage = lazy(() => import("@/routes/AuthPage").then((mod) => ({ default: mod.AuthPage })));
const EmptyNoGoalsPage = lazy(() => import("@/routes/EmptyNoGoalsPage").then((mod) => ({ default: mod.EmptyNoGoalsPage })));
const LandingPage = lazy(() => import("@/routes/LandingPage").then((mod) => ({ default: mod.LandingPage })));
const NotFoundPage = lazy(() => import("@/routes/NotFoundPage").then((mod) => ({ default: mod.NotFoundPage })));
const OnboardingWizardPage = lazy(() => import("@/routes/OnboardingWizardPage").then((mod) => ({ default: mod.OnboardingWizardPage })));
const ReportsPage = lazy(() => import("@/routes/ReportsPage").then((mod) => ({ default: mod.ReportsPage })));
const SuperAdminPage = lazy(() => import("@/routes/SuperAdminPage").then((mod) => ({ default: mod.SuperAdminPage })));
const SettingsIntegrationsPage = lazy(() => import("@/routes/SettingsIntegrationsPage").then((mod) => ({ default: mod.SettingsIntegrationsPage })));
const SettingsAiPage = lazy(() => import("@/routes/SettingsAiPage").then((mod) => ({ default: mod.SettingsAiPage })));
const SettingsNotificationsPage = lazy(() => import("@/routes/SettingsNotificationsPage").then((mod) => ({ default: mod.SettingsNotificationsPage })));
const SettingsPage = lazy(() => import("@/routes/SettingsPage").then((mod) => ({ default: mod.SettingsPage })));
const SettingsProfilePage = lazy(() => import("@/routes/SettingsProfilePage").then((mod) => ({ default: mod.SettingsProfilePage })));
const TaskDetailsPage = lazy(() => import("@/routes/TaskDetailsPage").then((mod) => ({ default: mod.TaskDetailsPage })));
const TasksPage = lazy(() => import("@/routes/TasksPage").then((mod) => ({ default: mod.TasksPage })));
const TeamMembersPage = lazy(() => import("@/routes/TeamMembersPage").then((mod) => ({ default: mod.TeamMembersPage })));
const TodayPage = lazy(() => import("@/routes/TodayPage").then((mod) => ({ default: mod.TodayPage })));
const WorkspacePickerPage = lazy(() => import("@/routes/WorkspacePickerPage").then((mod) => ({ default: mod.WorkspacePickerPage })));
const WorkspaceSetupPage = lazy(() => import("@/routes/WorkspaceSetupPage").then((mod) => ({ default: mod.WorkspaceSetupPage })));

const PageFallback = (): JSX.Element => <main className="page"><div className="card skeleton h-28 rounded-xl" /></main>;

const APP_OPEN_STORAGE_KEY = "taskkar-analytics-last-open-at";
const DAILY_PING_PREFIX = "taskkar-analytics-daily-ping";

const readStorage = (key: string): string | undefined => {
  try {
    const value = window.localStorage.getItem(key);
    return value && value.length > 0 ? value : undefined;
  } catch {
    return undefined;
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // no-op
  }
};

const computeDaysSince = (iso: string | undefined): number | undefined => {
  if (!iso) return undefined;
  const previous = new Date(iso).getTime();
  if (!Number.isFinite(previous)) return undefined;
  const diffMs = Date.now() - previous;
  if (!Number.isFinite(diffMs) || diffMs < 0) return undefined;
  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
};

const AnalyticsBridge = (): null => {
  const location = useLocation();
  const authUser = useAppStore((state) => state.authUser);
  const workspace = useAppStore((state) => state.workspace);

  const openTrackedRef = useRef(false);
  const openMetadataRef = useRef<{ isReturningUser: boolean; daysSinceLastOpen?: number }>({ isReturningUser: false });

  useEffect(() => {
    void initAnalytics();
  }, []);

  useEffect(() => {
    const previousOpenAt = readStorage(APP_OPEN_STORAGE_KEY);
    const daysSinceLastOpen = computeDaysSince(previousOpenAt);
    const isReturningUser = typeof previousOpenAt === "string";
    openMetadataRef.current = { isReturningUser, daysSinceLastOpen };

    setAnalyticsContext({
      is_returning_user: isReturningUser
    });

    if (!openTrackedRef.current) {
      openTrackedRef.current = true;
      trackEvent("app_opened", {
        is_returning_user: isReturningUser,
        days_since_last_open: daysSinceLastOpen
      });
    }

    writeStorage(APP_OPEN_STORAGE_KEY, new Date().toISOString());
  }, []);

  useEffect(() => {
    const routeTemplate = normalizeRouteTemplate(location.pathname);
    const platformRole = authUser?.platformRole;

    setAnalyticsContext({
      surface: routeTemplate,
      route_template: routeTemplate,
      workspace_id: workspace.id || undefined,
      user_role: platformRole,
      workspace_team_size: workspace.teamSize,
      workspace_industry: workspace.industry || undefined,
      is_authenticated: Boolean(authUser),
      is_returning_user: openMetadataRef.current.isReturningUser
    });
  }, [authUser, location.pathname, workspace.id, workspace.industry, workspace.teamSize]);

  useEffect(() => {
    if (authUser?.platformRole === "platform_admin") return;
    trackPageView({
      pathname: location.pathname,
      search: location.search,
      title: document.title,
      surface: normalizeRouteTemplate(location.pathname)
    });
  }, [authUser?.platformRole, location.pathname, location.search]);

  useEffect(() => {
    if (authUser?.platformRole === "platform_admin") return;
    setAnalyticsUser(authUser?.id);
    setAnalyticsUserProperties({
      user_role: authUser?.platformRole,
      workspace_id: workspace.id || undefined,
      workspace_team_size: workspace.teamSize,
      workspace_industry: workspace.industry || undefined,
      is_authenticated: Boolean(authUser),
      is_returning_user: openMetadataRef.current.isReturningUser
    });
  }, [authUser, workspace.id, workspace.industry, workspace.teamSize]);

  useEffect(() => {
    if (!authUser || !workspace.id) return;
    if (authUser.platformRole === "platform_admin") return;

    const today = new Date().toISOString().slice(0, 10);
    const key = `${DAILY_PING_PREFIX}:${workspace.id}`;
    const last = readStorage(key);
    if (last === today) return;

    trackEvent("daily_active_ping", {
      workspace_id: workspace.id,
      is_returning_user: openMetadataRef.current.isReturningUser
    });
    writeStorage(key, today);
  }, [authUser, workspace.id]);

  return null;
};

const RequireAuth = (): JSX.Element => {
  const { pathname } = useLocation();
  const authUser = useAppStore((state) => state.authUser);
  const workspaceQuery = useQuery({
    queryKey: ["api", "workspace-picker-gate"],
    queryFn: apiClient.getWorkspaces,
    enabled: Boolean(authUser),
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });

  if (!authUser) return <Navigate to="/auth" replace />;
  const isOnboardingRoute = pathname.startsWith("/onboarding");
  const isWorkspacePickerRoute = pathname === "/workspace-picker";
  if (isOnboardingRoute && !isWorkspacePickerRoute && (workspaceQuery.data?.length ?? 0) > 1) {
    return <Navigate to="/workspace-picker" replace />;
  }
  return <Outlet />;
};

const RequirePlatformAdmin = (): JSX.Element => {
  const authUser = useAppStore((state) => state.authUser);
  if (!authUser) return <Navigate to="/auth" replace />;
  if (authUser.platformRole !== "platform_admin") return <Navigate to="/today" replace />;
  return <Outlet />;
};

export const App = (): JSX.Element => {
  useTheme();
  const setAuthUser = useAppStore((state) => state.setAuthUser);
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: 1
          }
        }
      }),
    []
  );

  useEffect(() => {
    setApiAuthFailureHandler(() => {
      setAuthUser(null);
    });
    return () => setApiAuthFailureHandler(undefined);
  }, [setAuthUser]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AnalyticsBridge />
        <BackendSyncBridge />
        <AppShell>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route element={<RequireAuth />}>
                <Route path="/workspace-picker" element={<WorkspacePickerPage />} />
                <Route path="/onboarding/workspace" element={<WorkspaceSetupPage />} />
                <Route path="/onboarding/wizard" element={<OnboardingWizardPage />} />

                <Route path="/today" element={<TodayPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/projects" element={<Navigate to="/tasks" replace />} />
                <Route path="/tasks/:taskId" element={<TaskDetailsPage />} />
                <Route path="/activity" element={<ActivityPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/agents" element={<AgentsPage />} />
                <Route path="/agents/:agentId" element={<AgentDetailsPage />} />

                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/profile" element={<SettingsProfilePage />} />
                <Route path="/settings/ai" element={<SettingsAiPage />} />
                <Route path="/settings/notifications" element={<SettingsNotificationsPage />} />
                <Route path="/settings/integrations" element={<SettingsIntegrationsPage />} />
                <Route path="/settings/team" element={<TeamMembersPage />} />
                <Route path="/settings/archive" element={<ArchivePage />} />

                <Route path="/empty/no-goals" element={<EmptyNoGoalsPage />} />
              </Route>
              <Route element={<RequirePlatformAdmin />}>
                <Route path="/superadmin" element={<SuperAdminPage />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </AppShell>
        <ToastHost />
      </BrowserRouter>
    </QueryClientProvider>
  );
};
